import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { apiUrl } from "../../../utils/config";
import { ScrollText, Search, Download, Trash2, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";

const API      = apiUrl;
const PAGE_SIZE = 25;

const ACTION_COLORS = {
  "Created":        "bg-emerald-100 text-emerald-800",
  "Updated":        "bg-blue-100 text-blue-800",
  "Deleted":        "bg-red-100 text-red-800",
  "Changed Status": "bg-amber-100 text-amber-800",
};

const ENTITY_COLORS = {
  "Product":     "text-orange-600",
  "Category":    "text-purple-600",
  "Order":       "text-blue-600",
  "Blog Post":   "text-pink-600",
  "Role":        "text-indigo-600",
  "Settings":    "text-gray-600",
  "Table":       "text-teal-600",
  "Reservation": "text-amber-600",
  "Inventory":   "text-lime-700",
  "Gift Card":   "text-rose-600",
  "Staff":       "text-cyan-600",
};

const fmt = (iso) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

const unique = (arr) => [...new Set(arr)].sort();

const exportCSV = (logs) => {
  const header = ["Timestamp", "User", "Email", "Action", "Entity", "Target"];
  const rows   = logs.map((l) =>
    [l.created_at, l.user_name, l.user_email, l.action, l.entity, l.target]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  const blob = new Blob([header.join(",") + "\n" + rows.join("\n")], { type: "text/csv" });
  const a    = document.createElement("a");
  a.href     = URL.createObjectURL(blob);
  a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
};

const AuditLogPage = () => {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [entity,  setEntity]  = useState("All");
  const [action,  setAction]  = useState("All");
  const [date,    setDate]    = useState("");
  const [page,    setPage]    = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audit-logs?limit=1000`);
      setLogs(res.data?.list || []);
    } catch (_) {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const clearAll = async () => {
    if (!window.confirm("Clear all audit log entries? This cannot be undone.")) return;
    await axios.delete(`${API}/audit-logs`);
    setLogs([]);
    setPage(1);
  };

  const entities = useMemo(() => unique(logs.map((l) => l.entity)), [logs]);
  const actions  = useMemo(() => unique(logs.map((l) => l.action)),  [logs]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return logs.filter((l) => {
      if (entity !== "All" && l.entity !== entity) return false;
      if (action !== "All" && l.action !== action) return false;
      if (date && l.created_at?.slice(0, 10) !== date) return false;
      if (q && !l.user_name?.toLowerCase().includes(q) &&
               !l.target?.toLowerCase().includes(q) &&
               !l.entity?.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [logs, search, entity, action, date]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const today = new Date().toISOString().slice(0, 10);
  const todayCount = useMemo(() => logs.filter((l) => l.created_at?.slice(0, 10) === today).length, [logs]);

  const inp = "border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 transition bg-white";

  return (
    <div className="p-6 space-y-5">

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
            <ScrollText size={20} className="text-orange-600" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">Audit Log</h1>
            <p className="text-xs text-gray-400 mt-0.5">
              {logs.length} total entries · {todayCount} today
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={() => exportCSV(filtered)}
            disabled={filtered.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600 disabled:opacity-40 transition"
          >
            <Download size={14} /> Export CSV
          </button>
          <button
            onClick={clearAll}
            disabled={logs.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-500 rounded-xl text-sm font-semibold hover:bg-red-50 disabled:opacity-40 transition"
          >
            <Trash2 size={14} /> Clear All
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className={inp + " pl-8 w-full"}
            placeholder="Search user, entity, target…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <select className={inp} value={entity} onChange={(e) => { setEntity(e.target.value); setPage(1); }}>
          <option value="All">All Entities</option>
          {entities.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <select className={inp} value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}>
          <option value="All">All Actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input
          type="date"
          className={inp}
          value={date}
          onChange={(e) => { setDate(e.target.value); setPage(1); }}
        />
        {(search || entity !== "All" || action !== "All" || date) && (
          <button
            onClick={() => { setSearch(""); setEntity("All"); setAction("All"); setDate(""); setPage(1); }}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-800 font-semibold transition"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-300 gap-2">
            <RefreshCw size={22} className="animate-spin" />
            <span className="text-sm text-gray-400">Loading…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-gray-300 gap-3">
            <ScrollText size={36} />
            <p className="text-sm font-medium text-gray-400">
              {logs.length === 0 ? "No activity recorded yet." : "No entries match your filters."}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-3 text-left">Time</th>
                    <th className="px-4 py-3 text-left">Admin</th>
                    <th className="px-4 py-3 text-left">Action</th>
                    <th className="px-4 py-3 text-left">Entity</th>
                    <th className="px-4 py-3 text-left">Target</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {pageItems.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">
                        {fmt(log.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-800 leading-tight">{log.user_name}</p>
                        {log.user_email && <p className="text-xs text-gray-400">{log.user_email}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${ACTION_COLORS[log.action] || "bg-gray-100 text-gray-700"}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${ENTITY_COLORS[log.entity] || "text-gray-700"}`}>
                          {log.entity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={log.target}>
                        {log.target}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
                <span>
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="px-2 font-semibold text-gray-700">{page} / {totalPages}</span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogPage;
